import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';

import { Button } from '@shared/ui/button';
import { Heading } from '@shared/ui/heading';
import { ModalWrapper } from '@shared/ui/modal-wrapper';
import IconX from '@assets/img/icons/close-x.svg';

import { ModalWindowProps } from './types';

import styles from './styles.module.scss';

/**
 * Модальное окно
 */
export const ModalWindow: FC<PropsWithChildren<ModalWindowProps>> = (props) => {
	const {
		isShow: isShowProp,
		title,
		header,
		doClose,
		modalStyles,
		modalClassName,
		modalHeaderClassName,
		modalBodyClassName,
		isFullScreenMobile,
		isLoading,
		children,
	} = props;

	const [isShow, setIsShow] = useState(isShowProp);

	/* синхронизация при изменении внешнего флага */
	useEffect(() => {
		setIsShow(isShowProp);
	}, [isShowProp]);

	const handleClose = useCallback(() => {
		setIsShow(false);
		doClose?.(false);
	}, [doClose]);

	/* === Esc‑listener === */
	useEffect(() => {
		const handleKeyDown = (evt: KeyboardEvent) => {
			if (evt.key === 'Escape' && isShow) {
				evt.stopPropagation();
				handleClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isShow, handleClose]);

	const headerNode = useMemo(() => {
		if (header) return header;

		return (
			<div className={styles.HeaderContent}>
				<Heading noMargin className={styles.ModalTitle} size="h2">
					{title}
				</Heading>
				<Button buttonType="icon" className={styles.CloseIco} svg={IconX} onClick={handleClose} />
			</div>
		);
	}, [header, title, handleClose]);

	const loader = isLoading ? (
		<>
			<div className={styles.OverlayLoader} />
			<div className={styles.Loader} />
		</>
	) : null;

	if (!isShow) return null;

	return (
		<ModalWrapper>
			{loader}
			<div
				className={cn(
					styles.Wrapper,
					{ [styles.LoaderHide]: isLoading },
					{ [styles.FullScreenMobile]: isFullScreenMobile }
				)}
			>
				<div className={styles.AlignContainer}>
					<div className={styles.Overlay} onClick={handleClose} />
					<div className={styles.AlignContent}>
						<div className={`${styles.Window} ${modalClassName ?? ''}`} style={modalStyles}>
							<div className={`${styles.Header} ${modalHeaderClassName ?? ''}`}>{headerNode}</div>

							<div className={`${styles.Body} ${modalBodyClassName ?? ''}`}>{children}</div>
						</div>
					</div>
				</div>
			</div>
		</ModalWrapper>
	);
};
